<template>
    <v-container
        class="pa-12 fill-height align-start"
        fluid
    >
        <template v-if="loading">
            <v-overlay :value="loading">
                <v-progress-circular
                    :size="100"
                    color="white"
                    indeterminate
                ></v-progress-circular>
            </v-overlay>
        </template>
        <template v-else>
            <v-row>
                <v-col class="d-flex align-center">
                    <div class="headline mr-4">States</div>
                    <v-btn small depressed color="primary" @click="createStateForm = true">Create state</v-btn>
                </v-col>
            </v-row>
            <v-card
                outlined
                width="660"
                class="mt-4"
            >
                <template v-if="states.length > 0">
                    <template v-for="(state, index) in states">
                        <v-row 
                            class="px-4"
                            align="center"
                            :key="`A${index}`"
                        >
                            <v-col cols="8" class="flex-grow-1 flex-shrink-0 py-2">
                                <div class="subtitle-2" v-text="state.name"></div>
                            </v-col>
                            <v-col cols="4" class="flex-grow-0 flex-shrink-0 text-right py-2">
                                <v-btn 
                                    icon
                                    color="primary" 
                                    class="mr-2" 
                                    @click="updateItemEventHandler(state)"
                                ><v-icon>mdi-pencil</v-icon></v-btn>
                                <v-btn 
                                    icon
                                    color="secondary" 
                                    @click="deleteItemEventHandler(state.id)"
                                ><v-icon>mdi-trash-can</v-icon></v-btn>
                            </v-col>
                        </v-row>
                        <v-divider 
                            v-if="index + 1 < states.length"
                            :key="`B${index}`"
                        ></v-divider>
                    </template>
                </template>
                <template v-else>
                    <div class="subtitle-2 pa-4">No results</div>
                </template>
            </v-card>
        </template>
        <!-- create state bottom sheet -->
        <v-bottom-sheet v-model="createStateForm">
            <v-sheet class="text-center">                
                <v-row no-gutters justify="center" class="py-6">
                    <v-col cols="12" md="8" lg="4">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    label="State name"
                                    v-model="newStateObject.name"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    label="State label"
                                    v-model="newStateObject.label"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-select
                                    dense
                                    outlined
                                    hide-details
                                    class="mb-2"
                                    label="Country"
                                    :items="countries"
                                    item-text="name"
                                    item-value="id"
                                    v-model="newStateObject.countryId"
                                    v-if="!loadingCountries"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <div class="d-flex">
                            <v-btn 
                                depressed 
                                small 
                                color="primary"
                                @click="createState"
                                :disabled="creatingState"
                                :loading="creatingState"
                            >Save</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                text
                                small
                                color="secondary"
                                @click="createStateForm = false"
                            >close</v-btn>
                        </div>
                    </v-col>                                           
                </v-row>              
            </v-sheet>
        </v-bottom-sheet>
        <!-- Update state bottom sheet -->
        <v-bottom-sheet v-model="updateStateForm">
            <v-sheet class="text-center">                
                <v-row no-gutters justify="center" class="py-6">
                    <v-col cols="12" md="8" lg="4">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    label="State name"
                                    v-model="updateStateObject.name"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    label="State label"
                                    v-model="updateStateObject.label"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-select
                                    dense
                                    outlined
                                    hide-details
                                    class="mb-2"
                                    label="Country"
                                    :items="countries"
                                    item-text="name"
                                    item-value="id"
                                    v-model="updateStateObject.countryId"
                                    v-if="!loadingCountries"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <div class="d-flex">
                            <v-btn 
                                depressed 
                                small 
                                color="primary"
                                @click="updateState"
                                :disabled="updatingState"
                                :loading="updatingState"
                            >Update</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                text
                                small
                                color="secondary"
                                @click="updateStateForm = false"
                            >close</v-btn>
                        </div>
                    </v-col>                                           
                </v-row>              
            </v-sheet>
        </v-bottom-sheet>
        <!-- Delete state -->
        <v-bottom-sheet v-model="deleteStateSheet">
            <v-sheet class="text-center py-4">
                <div class="py-3">Are you sure you want to delete this state?</div>
                <v-btn
                    text
                    color="secondary"
                    @click="deleteState"
                    :disabled="deletingState"
                    :loading="deletingState"
                >Yes</v-btn>
                <v-btn
                    text
                    color="primary"
                    @click="deleteStateSheet = false"
                >No</v-btn>
            </v-sheet>
        </v-bottom-sheet> 
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'

    export default {
        name: 'states',
        data: () => ({
            states: [],
            countries: [],
            newStateObject: {
                name: null,
                label: null,
                countryId: 0
            },
            updateStateObject: {
                id: 0,
                name: null,
                label: null,
                languageId: 0,
                countryId: 0
            },
            deleteStateById: 0,
            snackbar: {
                show: true,
                message: ''
            },
            createStateForm: false,
            updateStateForm: false,
            deleteStateSheet: false,
            loadingCountries: true,
            creatingState: false,
            updatingState: false,
            deletingState: false,
            loading: true
        }),
        computed: {
            ...mapGetters(['languages', 'defaultLanguage'])
        },
        mounted() {
            this.fetchStates()
            this.fetchCountries()
        },
        methods: {
            async fetchStates() {
                await this.axios.get(`/State/GetAll?languageId=${this.defaultLanguage.id}`)
                .then(response => {
                    this.states = response.data.list
                    this.loading = false
                })
                .catch(error => {
                    this.snackbar.message = error 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loading = false
                })
            },
            async fetchCountries() {
                await this.axios.get(`/Country/GetAll?languageId=${this.defaultLanguage.id}`)
                .then(response => {
                    this.countries = response.data.list
                    this.loadingCountries = false
                })
                .catch(error => {
                    this.snackbar.message = error 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.loadingCountries = false
                })
            },
            createState() {
                this.creatingState = true
                const data = {
                    name: this.newStateObject.name,
                    label: this.newStateObject.label,
                    languageId: this.defaultLanguage.id,
                    countryId: this.newStateObject.countryId
                }
                this.axios.post('/State/CreateState', data)
                .then(response => {
                    this.states.push(response.data)
                    this.creatingState = false
                    this.createStateForm = false
                    this.snackbar.message = 'State created' 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
                .catch(error => {
                    this.snackbar.message = error 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.creatingState = false
                })
            },
            updateItemEventHandler(state) {
                this.updateStateObject.id = state.id
                this.updateStateObject.name = state.name
                this.updateStateObject.label = state.label
                this.updateStateObject.languageId = state.languageId
                this.updateStateObject.countryId = state.countryId
                this.updateStateForm = true
            },
            updateState() {
                this.updatingState = true
                const data = {
                    id: this.updateStateObject.id,
                    name: this.updateStateObject.name,
                    label: this.updateStateObject.label,
                    languageId: this.updateStateObject.languageId,
                    countryId: this.updateStateObject.countryId
                }
                this.axios.post('/State/UpdateState', data)
                .then(response => {
                    console.log(response.data)
                    const index = this.states.map(x => x.id).indexOf(this.updateStateObject.id)
                    this.states[index] = response.data
                    this.updatingState = false
                    this.updateStateForm = false
                })
                .catch(error => {
                    this.updatingState = false
                    this.snackbar.message = error 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            },
            deleteItemEventHandler(id) {
                this.deleteStateById = id
                this.deleteStateSheet = true
            },
            deleteState() {
                this.deletingState = true
                this.axios.delete(`/State/Delete?id=${this.deleteStateById}`)
                .then(response => {
                    const index = this.states.map(x => x.id).indexOf(this.deleteStateById)
                    this.states.splice(index, 1)
                    this.deletingState = false
                    this.deleteStateSheet = false
                })
                .catch(error => {
                    this.deletingState = false
                    this.snackbar.message = error 
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            }
        }
    }
</script>

<style lang="scss" scoped>

</style>